import React, { useEffect, useState } from 'react'
import backgroundImage from '../../images/bgd/bg-mobile-new.png'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'
import { MedalIcon } from '../../svgIcons/MedalIcon'
import { useDebounce } from 'usehooks-ts'
import { SearchIcon } from '../../svgIcons/SearchIcon'
import { HelptipIcon } from '../../svgIcons/HelptipIcon'
// import { fakeData } from './fakeData'
import { humanReadableHashExtended } from '../../blockchainUtils/general'
import { formatUSD } from '../../blockchainUtils/math'
import { monthsAgo } from '../../utils'
import Spinner from '../Spinner'
import { FirstPlaceIcon } from '../../svgIcons/FirstPlaceIcon'
import { SecondPlaceIcon } from '../../svgIcons/SecondPlaceIcon'
import { ThirdPlaceIcon } from '../../svgIcons/ThirdPlaceIcon'
import { CustomBreakpoints, useCustomBreakpoint } from '../charts/Charts'
import Popup from '../Popup'
import { BraavosLeaderboardIcon } from '../../svgIcons/BraavosLeaderboardIcon'
import { LeaderboardRank, loadLeaderboardData } from './helper'
import { useWalletContext } from '../WalletContext'
import { PlusIcon } from '../../svgIcons/PlusIcon'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BackIcon } from '../../svgIcons/BackIcon'
import { MinusIcon } from '../../svgIcons/MinusIcon'

const FAQQuestions = [
    {
        title: 'How are points calculated?',
        response: `Points are calculated for each user account by the lifetime fees accrued by the account. 
        This includes live positions and closed positions. 
        The fees are converted to USD and aggregated across all account positions. Some factors may be applied for each position, based on the specific pool, the time the position was minted, being an active mySwap community member, account security level, references and other factors. The result is a number which is then used for ranking the account.
        <br><br>* The calculation formula should be treated as WIP at present and may change at any time without prior notice.
        <br>* Conversion of fees to USD will be done per position, using the rates at the time of position closing, or at current rates if position is still open
        `
    },
    {
        title: 'How does the mint time impact my points?',
        response: `Each position has a mint time, the time in which the position was initially created. The older the position is, the higher its factor potentially is.<br>
        <br>- All positions minted before Feb 1st 2024 have a modifier of X2
        <br>- All positions minted before March 1st 2024 (but after Feb 1st 2024, inclusive) have a modifier of X1.5
        <br>- All positions minted before April 1st 2024 (but after Match 1st 2024, inclusive) have a modifier of X1.25
        `
    },
    {
        title: `How does the position's pool impact my points?`,
        response: `Different pools have different token pairs and fees. We have factors for special pools which get special incentives.
        The special pools are: 
        USDC/USDT, ETH/USDC, XXXX
        USDC/USDT: modifier of X2
        ETH/USDC: modifier of X1.5<br><br>
        The fees of the pool also impact the points calculation. Lower fee pools get a higher factor, to incentivize adding liquidity also to lower-fee pools.
        <br>
        <br>Fee of 0.001%: modifier of X1000
        <br>Fee of 0.01%: modifier of X100
        <br>Fee of 0.05%: modifier of X20
        <br>Fee of 0.3%: modifier of X3.3
`
    },
    {
        title: 'What are the best practices for gaining higher ranking?',
        response: `In order to earn points, you need to make sure your positions are active, meaning they are balanced around the current price. In case you have an out-of-range position, it will not be earning points (nor fees) until you rebalance it. Luckily, you can now rebalance in a single click, and no math diploma required for the calculations. Just click the 'Rebalance' button in the top right corner of your position card <image>\n\n
        More tips:
- The more concentrated your position is, the more fees and points you will earn. (Note that in non-stable pools, when setting a narrow range, you also have a larger risk of falling out of range).
- Minting your positions early will give you a higher factor for the lifetime of the position. Rebalancing a position does not change the factor - so you can keep the same old position working for you with a high factor, as long as you keep rebalancing it.





`
    }
]

// export type Rank = { address: string; apr: number; amount: number; numPositions: number; points: number; isBraavosWallet?: boolean }
const allHeaderCells = [
    { title: 'Ranking', helptip: undefined }, //, helptip: 'Ranking of Account' },
    { title: 'Account', helptip: undefined }, //, helptip: 'Account Name or Address' },
    // { title: 'APR', helptip: 'Annual percentage return' },
    { title: 'Earned Fees', helptip: 'Lifetime fees earned' },
    { title: 'Fee Points', helptip: 'Points due to fees earned' },
    { title: 'Factor', helptip: 'Liquidity provided at an earlier stage secures a higher factor. Additionally, a premium factor is applied to select featured pools' },
    //{ title: 'Positions', helptip: 'How many positions this user invested in' },
    // { title: 'Time', helptip: 'Amount of time the user is active in myswap' },
    { title: 'Bonus Points', helptip: 'For users who have participated in the mySwap NFT campaigns' },
    { title: 'Total Points', helptip: undefined } // 'Total points for account, after applying the factor and bonus points' }
]
const SKIP_PER_PAGE = 25
export function Leaderboard() {
    const { walletAddress: connectedAddress, connect } = useWalletContext()
    let [searchParams] = useSearchParams()
    const screenSize = useCustomBreakpoint()
    const [serachText, setSearchText] = useState('')
    const debouncedSearchText = useDebounce(serachText, 1000)
    const [leadershipData, setLeadershipData] = useState([] as LeaderboardRank[])
    const [allRankings, setAllRankings] = useState(undefined as unknown as Record<number, LeaderboardRank[]>)
    // const [allRankings, setAllRankings] = useState(undefined as unknown as LeaderboardRank[])
    const [isFiltering, setIsFiltering] = useState(false)
    const [userData, setUserData] = useState(undefined as unknown as LeaderboardRank)
    const [isFaqOpen, setIsFaqOpen] = useState(false)
    const [selectedRankMobilePopupOpen, setSelectedRankMobilePopupOpen] = useState(undefined as unknown as LeaderboardRank)
    const [faqQuestionOpened, setFaqQuestionOpened] = useState(-1)
    const [totalPages, setTotalPages] = useState(10)
    const [totalRanks, setTotalRanks] = useState(undefined as unknown as number)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const navigate = useNavigate()
    const [page, setPage] = useState(undefined as unknown as number)

    // useEffect(() => {
    //     connect(true)
    // }, [])

    useEffect(() => {
        if (searchParams.get('isFaqOpen') && searchParams.get('isFaqOpen')) setIsFaqOpen(true)
        else setIsFaqOpen(false)
        const pageNum = searchParams.get('page') ?? 1
        setPage(Number(pageNum))
    }, [searchParams])

    useEffect(() => {
        if (page) loadData()
    }, [connectedAddress, page])

    useEffect(() => {
        onFilterBySearch()
    }, [debouncedSearchText])

    async function loadData() {
        // if (isLoadingMore) return
        setIsLoadingMore(true)
        if (allRankings && allRankings[SKIP_PER_PAGE * (page - 1)]) {
            return sortData(allRankings[SKIP_PER_PAGE * (page - 1)])
        }
        const response = await loadLeaderboardData(connectedAddress || '', SKIP_PER_PAGE * (page - 1), SKIP_PER_PAGE)
        setUserData(response.user || (undefined as unknown as LeaderboardRank))
        if (response.list && response.list.length > 0) {
            sortData([...response.list])
            setAllRankings({ ...allRankings, [page]: response.list })
            setIsLoadingMore(false)
            setTotalPages(Number((response.n / SKIP_PER_PAGE).toFixed(0)))
            setTotalRanks(response.n)
        }
    }

    function getColumns() {
        if (screenSize >= CustomBreakpoints.Large) return ['Ranking', 'Account', 'Earned Fees', 'Fee Points', 'Factor', 'Bonus Points', 'Total Points']
        return ['Ranking', 'Account', 'Total Points']
    }

    function sortData(data: LeaderboardRank[]) {
        if (!data) return
        // const res = data.sort((a, b) => b.points - a.points)
        // const newList = data.slice((page - 1) * 100, page * 100)
        // console.log('newList ', newList)
        setLeadershipData(data)
        setIsFiltering(false)
        setIsLoadingMore(false)
    }

    async function onFilterBySearch() {
        if (!debouncedSearchText && allRankings && allRankings[page]) return sortData(allRankings[page])
        if (!allRankings) return
        setIsFiltering(true)
        const allDataSpread = [] as unknown as LeaderboardRank[]
        Object.values(allRankings).map((batch) =>
            batch.map((item) => {
                allDataSpread.push(item)
            })
        )

        const allRelevantRanks = allDataSpread.filter((rank) => {
            if (rank.address.toLowerCase().includes(debouncedSearchText.toLowerCase())) return rank
            if (rank.name && rank.name.toLowerCase().includes(debouncedSearchText.toLowerCase())) return rank
        })
        setLeadershipData(allRelevantRanks)
        setIsFiltering(false)
    }

    function renderMobilePopupColumnLineBreakdown(title: string, helptip: string | undefined, content: string, className?: string, contentClassName?: string) {
        return (
            <div className={`w-full ${className ? className : 'mb-[18px]'} justify-between flex items-center`}>
                <div className='relative flex items-center'>
                    <span>{title}</span>
                    <div className='ml-2 group inline-flex'>
                        {helptip && <HelptipIcon className='text-[#8387A5] w-4 h-4' />}
                        <span className='left-[0px] lg:left-[-20px] top-[-45px] w-[200px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0 top-[30px] p-3 z-[1]'>
                            {helptip}
                        </span>
                    </div>
                </div>
                <span className={`${contentClassName ? contentClassName : ''}`}>{content}</span>
            </div>
        )
    }

    function renderUserRank(rank: number, className?: string) {
        return (
            <>
                {rank > 3 ? <span className={`${className ? className : 'pl-[20px]'} lg:text-[18px] text-[14px] text-white`}>{`#${rank.toLocaleString()}`}</span> : null}
                {rank === 1 ? (
                    <div className={`${className ? className : 'pl-[20px]'}`}>
                        <FirstPlaceIcon />
                    </div>
                ) : null}
                {rank === 2 ? (
                    <div className={`${className ? className : 'pl-[20px]'}`}>
                        <SecondPlaceIcon />
                    </div>
                ) : null}
                {rank === 3 ? (
                    <div className={`${className ? className : 'pl-[20px]'}`}>
                        <ThirdPlaceIcon />
                    </div>
                ) : null}
            </>
        )
    }

    function renderMobileColumnPopup() {
        if (screenSize >= CustomBreakpoints.Large || !selectedRankMobilePopupOpen) return null

        // const dateAgo = monthsAgo(new Date(selectedRankMobilePopupOpen.time))
        const rank = selectedRankMobilePopupOpen.ranking

        return (
            <Popup
                contentClassName='w-full'
                className='w-full'
                onClose={() => setSelectedRankMobilePopupOpen(undefined as unknown as LeaderboardRank)}
                open={Boolean(selectedRankMobilePopupOpen)}>
                <div className='w-full'>
                    <div className='flex items-center mb-4'>
                        {renderUserRank(selectedRankMobilePopupOpen.ranking, 'pl-0')}
                        <span className={`${rank > 3 ? 'text-white' : 'text-[#0AE8F9]'} ml-2 lg:text-[18px] text-[14px]`}>
                            {humanReadableHashExtended(selectedRankMobilePopupOpen.address || '', false)}
                        </span>
                    </div>

                    {/* {renderMobilePopupColumnLineBreakdown(
                        allHeaderCells[2].title,
                        allHeaderCells[2].helptip,
                        `${selectedRankMobilePopupOpen.apr.toString()}%`,
                        '',
                        'text-[#68F2B0]'
                    )} */}
                    {renderMobilePopupColumnLineBreakdown(allHeaderCells[2].title, allHeaderCells[2].helptip, `$${formatUSD(selectedRankMobilePopupOpen.fees)}`)}
                    {renderMobilePopupColumnLineBreakdown(allHeaderCells[3].title, allHeaderCells[3].helptip, selectedRankMobilePopupOpen.n.toString())}
                    {/* {renderMobilePopupColumnLineBreakdown(allHeaderCells[5].title, allHeaderCells[5].helptip, `${dateAgo} Months`)} */}
                    {renderMobilePopupColumnLineBreakdown(
                        allHeaderCells[4].title,
                        allHeaderCells[4].helptip,
                        selectedRankMobilePopupOpen.points.toString(),
                        'mb-[0px]',
                        'font-bold text-[#0AE8F9]'
                    )}
                </div>
            </Popup>
        )
    }

    function renderFAQLine(payload: { title: string; response: string }, index: number) {
        const { response, title } = payload
        const isSelected = index === faqQuestionOpened
        return (
            <div
                key={index}
                onClick={() => {
                    if (faqQuestionOpened !== index) setFaqQuestionOpened(index)
                    else setFaqQuestionOpened(-1)
                }}
                className={`flex w-full justify-between flex-1 cursor-pointer p-4 border-[1px] border-[#0C3F6D] rounded-[16px] ${index !== 0 ? 'mt-[16px]' : ''}`}
                style={{ background: 'transparent' }}>
                {/* style={{ background: isSelected ? 'white' : 'rgba(11, 10, 30, 0.70)' }}> */}
                {isSelected ? (
                    <div className='flex flex-col text-white'>
                        {/* <div className='flex flex-col text-black'> */}
                        <span className='font-[700] lg:text-[18px] text-[14px]'>{title}</span>
                        <span dangerouslySetInnerHTML={{ __html: response }} className='text-[16px] mt-2'>
                            {/* {response} */}
                        </span>
                    </div>
                ) : (
                    <span className='font-[700] text-white lg:text-[18px] text-[14px]'>{title}</span>
                )}
                {isSelected ? <MinusIcon className='text-white w-[14px] h-[14px] flex-shrink-0' /> : <PlusIcon className='text-white w-[14px] h-[14px] flex-shrink-0' />}
                {/* {isSelected ? <MinusIcon className='text-black w-[16px] h-[16px] flex-shrink-0' /> : <PlusIcon className='text-white w-[16px] h-[16px] flex-shrink-0' />} */}
            </div>
        )
    }

    function renderFAQ() {
        if (!isFaqOpen) return null
        const faqs = FAQQuestions.map(renderFAQLine)
        return (
            <div className='w-full lg:px-0 px-4'>
                <div className='cursor-pointer flex items-center mr-2 mb-2' onClick={() => navigate(`/leaderboard?page=${page}`)}>
                    <BackIcon className='w-[16px] h-[16px] mr-2' />
                    <span>Back</span>
                </div>
                <div className='flex p-4 lg:p-0 flex-col w-full items-center'>
                    <div className='flex flex-col'>
                        <span className='text-[24px] font-bold mt-4'>Overview</span>
                        <div className='text-[16px]'>
                            <span className='flex mt-4'>
                                The mySwap CL Leaderboard offers a dynamic and strategic platform for users to enhance their position within the mySwap ecosystem through an
                                innovative points-based system. This system is designed to reward users for their trading activities, specifically focusing on the lifetime fees
                                generated from both active and settled positions. Here's an overview:
                            </span>
                            <span className='flex font-bold mt-4'>Comprehensive Points Calculation:</span>
                            <span className='flex'>
                                {' '}
                                Points are based on the fees accrued in each position. These fees are converted into USD and are influenced by a variety of factors including the
                                liquidity pool's specifics, the initiation time of the position, active participation within the mySwap community, and the security measures of the
                                user's account. It's important to note that the calculation formula is subject to refinement and updates without prior notice.
                            </span>

                            <span className='flex font-bold mt-4'>Strategic Timing and Pool Selection:</span>
                            <span className='flex'>
                                The system incentivizes early position minting and strategic selection of liquidity pools. Positions created at earlier dates receive a higher
                                points multiplier, rewarding long-term participation. Similarly, engaging with specific pools, especially those with lower transaction fees or
                                designated as special, can significantly enhance points accumulation. This approach encourages a thoughtful and strategic engagement with the
                                platform's offerings.
                            </span>
                            <span className='flex font-bold mt-4'> Best Practices for Maximization:</span>
                            <span className='flex'>
                                Users are advised to maintain active and concentrated positions to optimize fee generation and, consequently, points accrual. The platform
                                simplifies this process through features like one-click rebalancing, allowing users to adjust their positions effortlessly to stay within the most
                                lucrative ranges. This leaderboard system is designed for users who are keen on maximizing their engagement and profitability within the mySwap
                                platform. It rewards foresight, strategic planning, and active participation, aligning user incentives with the overall health and dynamism of the
                                liquidity pools.
                            </span>
                        </div>
                    </div>
                    <span className='mt-[48px] font-[900] text-white text-[24px] leading-none text-center lg:text-[32px] mb-[48px]'>Frequently asked questions</span>
                    <div className='flex flex-col max-w-[614px] w-full'>{faqs}</div>
                </div>
            </div>
        )
    }

    function renderFAQPopup() {
        if (!isFaqOpen) return null

        return (
            <Popup
                contentClassName='w-full max-w-[528px] min-w-[528px]'
                className='w-full outline-none'
                onClose={() => navigate(`/leaderboard?page=${page}`)}
                open={Boolean(isFaqOpen)}>
                <div className='w-full pb-[100px] h-[500px] overflow-auto'>{renderFAQ()}</div>
            </Popup>
        )
    }

    function getTableHeaderCellNames(): { title: string; helptip?: string }[] {
        const existingColumns = getColumns()
        const columnsToUse: { title: string; helptip?: string | undefined }[] = []

        existingColumns.map((existingColumnName) => {
            const col = allHeaderCells.find((item) => item.title.toLowerCase() === existingColumnName.toLowerCase())
            if (col) columnsToUse.push(col)
        })

        //console.log("YSYS existingColumns=",existingColumns," columnsToUse=",columnsToUse)
        return columnsToUse
    }

    function renderPageDescription() {
        return (
            <div className='flex flex-col max-w-[800px]'>
                <div className='flex items-center'>
                    <MedalIcon className='w-[22px] h-[22px] mr-2' />
                    <span className='text-white text-[22px] font-bold'>mySwap Leaderboard</span>
                </div>
                <div className='mt-5'>
                    <span className='text-[16px]'>
                        mySwap Leaderboard ranks users by lifetime trading fees, rewarding strategic actions with points based on pool choice, timing, and community engagement.
                    </span>
                    <a href='https://myswap.gitbook.io/myswap-cl/myswap-leaderboard/' target='_blank' className='text-[#0AE8F9] text-[16px] ml-1 underline cursor-pointer'>
                        Learn more
                    </a>
                </div>
            </div>
        )
    }

    function renderSearchBox() {
        return (
            <div className='relative lg:w-[400px] w-[206px]'>
                <input
                    placeholder='Search by Account address'
                    value={serachText}
                    onChange={(e) => {
                        setSearchText(e.target.value)
                        setIsFiltering(true)
                    }}
                    className='w-full outline-none bg-transparent px-2 lg:px-4 py-2 border-[#8387A5] border-[1px] rounded-[8px] text-[#8387A5] text-[12px] lg:text-[16px]'></input>
                <SearchIcon className='absolute top-[10px] text-[#8387A5] right-[16px] lg:w-[18px] lg:h-[18px] w-[14px] h-[14px]' />
            </div>
        )
    }

    function renderHeaderTH(headerInfo: { title: string; helptip?: string }) {
        return (
            <th key={headerInfo.title}>
                <div className='flex relative items-center lg:mr-0 mr-6'>
                    <span>{headerInfo.title}</span>
                    <div className='ml-2 group inline-flex'>
                        {headerInfo.helptip && <HelptipIcon className='text-[#8387A5] w-4 h-4' />}
                        <span className='left-[-100px] lg:left-[-20px] top-[-45px] w-[200px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0 top-[30px] p-3 z-[1]'>
                            {headerInfo.helptip}
                        </span>
                    </div>
                </div>
            </th>
        )
    }

    function renderTableHeader() {
        const headerColumns = getTableHeaderCellNames()
        const headerTHS = headerColumns.map(renderHeaderTH)
        return (
            <thead>
                <tr className='border-b-[20px] border-transparent'>{headerTHS}</tr>
            </thead>
        )
    }

    function renderRanking(rank: number) {
        return <td className='pr-4'>{renderUserRank(rank, 'pl-[20px]')}</td>
    }

    function renderAddress(address: string, rank: number, isBraavosWallet?: boolean) {
        const shortenedAddress = humanReadableHashExtended(address || '', false)
        return (
            <td>
                <div className='flex'>
                    <span className={`lg:text-[18px] text-[14px] mr-1 ${rank <= 3 ? 'text-[#0AE8F9]' : 'text-white'}`}>{`${shortenedAddress}`}</span>
                    {isBraavosWallet ? <BraavosLeaderboardIcon /> : null}
                </div>
            </td>
        )
    }

    // function renderAPR(apr: number) {
    //     return (
    //         <td>
    //             <span className='text-[18px] text-[#68F2B0]'>{`${apr}%`}</span>
    //         </td>
    //     )
    // }

    function renderAmount(amount: number) {
        return (
            <td>
                <span className='lg:text-[18px] text-[14px] text-white'>{`$${formatUSD(amount)}`}</span>
            </td>
        )
    }

    /*function renderNumOfPositions(numOfPools: number) {
        return (
            <td>
                <span className='text-[18px] text-white'>{`${numOfPools}`}</span>
            </td>
        )
    }*/

    function renderFactor(factor: number) {
        const roundedFactor = Math.round(factor * 100) / 100
        const formattedFactor =
            roundedFactor <= 1
                ? '--'
                : 'x' +
                  roundedFactor
                      .toString()
                      .replace(/\.00$/, '')
                      .replace(/(\.\d)0$/, '$1')

        return (
            <td>
                <span className='lg:text-[18px] text-[14px] text-white'>{formattedFactor}</span>
            </td>
        )
    }
    function renderTime(time: string) {
        const dateAgo = monthsAgo(new Date(time))
        return (
            <td>
                <span className='lg:text-[18px] text-[14px] text-white'>{`${dateAgo} months`}</span>
            </td>
        )
    }

    function renderPoints(points: number, position: number) {
        return (
            <td>
                <span className={`lg:text-[18px] text-[14px] text-[#0AE8F9] ${position < 3 ? 'font-bold' : ''}`}>{`${points.toLocaleString()}`}</span>
            </td>
        )
    }

    function renderBonusNftPoints(points: number) {
        return (
            <td>
                <span className='lg:text-[18px] text-[14px] text-[#0AE8F9] '>{`${points.toLocaleString()}`}</span>
            </td>
        )
    }

    function renderMyAccountMissing() {
        if (!connectedAddress) return <tr></tr>
        return (
            <tr key='missing-acc' className='bg-[#ffffff0d] h-[70px] px-[30px] pt-[24px] pb-[17px]'>
                <td className='px-[30px] lg:text-[18px] text-[14px]'>{`#${(totalRanks - 1).toLocaleString()}`}</td>
                <td className='pr-4'>{renderAddress(connectedAddress!, -1, false)}</td>
                <td className='relative lg:min-w-0 min-w-[650px]'>
                    <span className='absolute top-0 lg:left-0 lg:w-[700px] flex h-full items-center justify-start'>
                        <span>You don't have any points. </span>
                        <span onClick={() => navigate('/positions')} className='text-[#0AE8F9] mx-1 underline cursor-pointer'>
                            Add some liquidity{' '}
                        </span>
                        <span>to participate in the leaderboard.</span>
                    </span>
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
            </tr>
        )
    }

    function renderTableRow(rankingData: LeaderboardRank, position: number, isMyAccount?: boolean) {
        const allRows = screenSize >= CustomBreakpoints.Large
        if (!rankingData && isMyAccount) return renderMyAccountMissing()
        // if (!position) return <tr key='missing'></tr>

        return (
            <tr
                onClick={() => {
                    rankingData ? setSelectedRankMobilePopupOpen(rankingData) : null
                }}
                key={position + 'myaccount'}
                className={`${position % 2 === 0 ? 'bg-[#ffffff0d]' : 'bg-[#ffffff1a]'} h-[70px] px-[30px] pt-[24px] pb-[17px]`}>
                {renderRanking(rankingData.ranking)}
                {renderAddress(rankingData.name || rankingData.address, rankingData.ranking, false)}
                {/* {renderAddress(rankingData.address, rankingData.isBraavosWallet)} */}
                {/* {screenSize >= CustomBreakpoints.Large ? renderAPR(rankingData.) : null} */}
                {allRows ? renderAmount(rankingData.fees) : null}
                {allRows ? renderPoints(rankingData.fee_points, position) : null}
                {allRows ? renderFactor(rankingData.time_factor * rankingData.pool_factor) : null}
                {/* {screenSize >= CustomBreakpoints.Large ? renderTime(rankingData.) : null} */}
                {allRows ? renderPoints(rankingData.v1_nft_points, position) : null}
                {renderPoints(rankingData.points, position)}
            </tr>
        )
    }

    function renderTableBody(isMyAccount?: boolean) {
        if (!leadershipData || leadershipData.length === 0 || isFiltering)
            return (
                <tbody className=''>
                    <tr>
                        <td>
                            <Spinner />
                        </td>
                        <td>
                            <Spinner />
                        </td>
                        <td>
                            <Spinner />
                        </td>
                        <td>
                            <Spinner />
                        </td>
                        <td>
                            <Spinner />
                        </td>
                        <td>
                            <Spinner />
                        </td>
                        <td>
                            <Spinner />
                        </td>
                    </tr>
                </tbody>
            )

        let rows: JSX.Element[] = []

        if (isMyAccount && connectedAddress) rows = [renderTableRow(userData || undefined, userData ? userData.ranking : 0, true)]
        else rows = leadershipData.map((item, index) => renderTableRow(item, index, false))

        return <tbody className='shadow-2xl lg:shadow-none rounded-[16px]'>{rows}</tbody>
    }

    function renderMyAccount() {
        if (serachText.length > 0 || leadershipData.length === 0 || !connectedAddress) return null
        return (
            <>
                <tbody>
                    <tr>
                        <td>
                            <div className='text-[12px] text-[#8387A5] mb-2'>My account</div>
                        </td>
                    </tr>
                </tbody>
                {renderTableBody(true)}
            </>
        )
    }

    function renderDataTable() {
        return (
            <table className='tablerounded w-full mt-5'>
                {renderTableHeader()}
                {renderMyAccount()}
                <tbody>
                    <tr>
                        <td>
                            <div className='text-[12px] text-[#8387A5] mb-2 mt-2'>{`${serachText.length > 0 ? 'Search results' : 'All accounts'}`}</div>
                        </td>
                    </tr>
                </tbody>
                {renderTableBody()}
            </table>
        )
    }

    function renderTableControls() {
        if (isLoadingMore)
            return (
                <div className='flex w-full justify-center items-center mt-4 h-[36px]'>
                    <Spinner />
                </div>
            )
        return (
            <div className='flex w-full justify-center items-center mt-4'>
                {page > 1 ? (
                    <span onClick={() => navigate(`/leaderboard?page=${page - 1}`)} className='text-[24px] font-bold cursor-pointer mr-2'>
                        {'<'}
                    </span>
                ) : null}
                {page > 4 ? (
                    <div className='flex items-center'>
                        <span onClick={() => navigate(`/leaderboard?page=${1}`)} className='cursor-pointer mr-2'>
                            1
                        </span>
                        <span className='mr-2'>...</span>
                        <span onClick={() => navigate(`/leaderboard?page=${page - 3}`)} className='cursor-pointer mr-2'>
                            {page - 3}
                        </span>
                        <span onClick={() => navigate(`/leaderboard?page=${page - 2}`)} className='cursor-pointer mr-2'>
                            {page - 2}
                        </span>
                        <span onClick={() => navigate(`/leaderboard?page=${page - 1}`)} className='cursor-pointer mr-2'>
                            {page - 1}
                        </span>
                    </div>
                ) : null}
                <span className='font-bold  mr-2'>{page}</span>
                {totalPages > page ? (
                    <span className='mr-2 cursor-pointer' onClick={() => navigate(`/leaderboard?page=${page + 1}`)}>
                        {page + 1}
                    </span>
                ) : null}
                {totalPages > page + 1 ? (
                    <span className='mr-2 cursor-pointer' onClick={() => navigate(`/leaderboard?page=${page + 2}`)}>
                        {page + 2}
                    </span>
                ) : null}
                {totalPages > page + 2 ? (
                    <span className='mr-2 cursor-pointer' onClick={() => navigate(`/leaderboard?page=${page + 3}`)}>
                        {page + 3}
                    </span>
                ) : null}
                {totalPages === page ? null : <span className='mr-2'>{'...'}</span>}
                {totalPages === page ? null : (
                    <span className='cursor-pointer mr-2' onClick={() => navigate(`/leaderboard?page=${totalPages}`)}>
                        {totalPages}
                    </span>
                )}
                {totalPages > page ? (
                    <span onClick={() => navigate(`/leaderboard?page=${page + 1}`)} className='text-[24px] font-bold cursor-pointer'>
                        {'>'}
                    </span>
                ) : null}
            </div>
        )
    }

    function renderContent() {
        if (isFaqOpen) return renderFAQ()

        return (
            <div className='w-full lg:px-0 px-4'>
                {renderPageDescription()}
                <div className='flex w-full mb-[16px] mt-[32px] items-center'>
                    {/* {renderSearchBox()} */}
                    {/* <div
                        className='cursor-pointer border-[1px] border-[#0C3F6D] rounded-[8px] bg-transparent h-[36px] px-2 flex items-center justify-center'
                        onClick={() => navigate('/leaderboard?isFaqOpen=true')}>
                        <>
                            <span>Learn more</span>
                            <HelptipIcon className='ml-1 text-white w-[14px] h-[14px]' />
                        </>
                    </div> */}
                    {connectedAddress ? (
                        <div className='cursor-pointer rounded-[8px] bg-[#366FB7] h-[36px] px-2 flex items-center justify-center' onClick={() => navigate('/create-position')}>
                            <>
                                <span>Add Liquidity to Get More Points</span>
                            </>
                        </div>
                    ) : null}
                </div>
                <div className='w-full overflow-auto'>{renderDataTable()}</div>
                {renderTableControls()}
            </div>
        )
    }

    return (
        <div
            className='h-full relative pb-[48px] lg:pb-0'
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                minHeight: '100vh'
            }}>
            {renderMobileColumnPopup()}
            {/* {renderFAQPopup()} */}
            <div className='lg:pt-6 w-full max-w-[98%] m-auto'>
                <Header selectedTab={'leaderboard'} className={''} />
            </div>
            <div className='container mx-auto'>
                <div className='flex w-full mt-[40px] pb-[80px]'>{renderContent()}</div>
            </div>
            <Footer selectedTab={'leaderboard'} />
        </div>
    )
}
